import moment from 'moment';
import React from 'react';
import { toast } from 'sonner';

import { ButtonBase, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PaymentIcon from '@material-ui/icons/Payment';

import ActionModal from 'components/ActionModal/ActionModal';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { payUserInvoice } from 'utils/api';

const useStyles = makeStyles((theme) => ({
  message: {
    width: 360,
    maxWidth: 'calc(100vw - 80px)',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'minmax(100px, auto) 1fr',
    gap: theme.spacing(1, 2),
    marginBottom: theme.spacing(3),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  value: {
    fontWeight: 600,
    textAlign: 'right',
  },
  paymentMethodNote: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: '18px',
    marginTop: -theme.spacing(1.5),
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

type Props = {
  invoice: any;
  open: boolean;
  onClose: () => void;
  onPaid?: () => Promise<void> | void;
  updateFuturePaymentMethod?: boolean;
};

const InvoicePaymentModal: React.FC<Props> = ({
  invoice, open, onClose, onPaid, updateFuturePaymentMethod = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { userInfo } = useUserInfoContext();
  const paymentMethods = userInfo?.agentInfo?.stripe?.paymentMethods ?? [];
  const defaultMethod = paymentMethods.find((method: any) => method.isDefault);
  const initialMethodId = invoice?.paymentMethod?.id || defaultMethod?.id || paymentMethods[0]?.id || '';
  const [paymentMethodId, setPaymentMethodId] = React.useState(initialMethodId);
  const [isPaying, setIsPaying] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setPaymentMethodId(initialMethodId);
  }, [initialMethodId, open]);

  if (!invoice) return null;

  const handlePay = async () => {
    setIsPaying(true);
    try {
      const result = await payUserInvoice(invoice.id, {
        paymentMethodId,
        updateFuturePaymentMethod,
      });
      toast.success('Invoice paid successfully.');
      if (result?.warning) toast.warning(result.warning);
      await onPaid?.();
      onClose();
    } catch (error: any) {
      const message = error.response?.data?.error
        || 'We could not process this payment. Check the selected card and try again.';
      toast.error(message);
    } finally {
      setIsPaying(false);
    }
  };

  const handleClose = () => {
    if (!isPaying) onClose();
  };

  return (
    open ? (
      <ActionModal
        backgroundColor={theme.palette.offWhite.main}
        textColor={theme.palette.darkGray.main}
        icon={<PaymentIcon />}
        onClose={handleClose}
        title="Pay Invoice"
        message={(
          <div className={classes.message}>
            <div className={classes.details}>
              <Typography className={classes.label}>Invoice Date</Typography>
              <Typography className={classes.value}>{moment.unix(invoice.created).format('MMM D, YYYY')}</Typography>
              <Typography className={classes.label}>Invoice Number</Typography>
              <Typography className={classes.value}>{invoice.number}</Typography>
            </div>
            <PaymentMethodSelector
              labelText="Payment Method"
              inputProps={{
                id: 'invoice-payment-method',
                value: paymentMethodId,
                disabled: isPaying,
                onChange: (event: React.ChangeEvent<{ value: unknown }>) => setPaymentMethodId(event.target.value as string),
              }}
            />
            {updateFuturePaymentMethod && (
              <Typography className={classes.paymentMethodNote}>
                This payment method will also be assigned to this product for future charges.
              </Typography>
            )}
          </div>
        )}
        buttons={[
          <ButtonBase disabled={isPaying} onClick={handleClose} className={classes.cancelButton}>Cancel</ButtonBase>,
          <ButtonBase disabled={!paymentMethodId || isPaying} onClick={handlePay}>
            {isPaying ? <CircularProgress size={18} color="inherit" /> : 'Pay Invoice'}
          </ButtonBase>,
        ]}
      />
    ) : null
  );
};

export default InvoicePaymentModal;
